import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Thermometer, Wind, Sun, Calendar, AlertTriangle } from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'
import { AsyncBoundary } from '@/components/AsyncBoundary'

interface WellnessSummary {
  consecutiveTravelDays: number
  fatigueScore: number
  upcomingClimateBlocks: { showId: string; city: string; reason: string }[]
  restDaysScheduled: number
}

interface ClimateProfile {
  minTempCelsius: number
  noSnowOrFrost: boolean
  outdoorBufferCelsius: number
  blockedMonths: number[]
  preferredMonths: number[]
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export function WellnessPage() {
  const qc = useQueryClient()

  const summary = useQuery<WellnessSummary, ApiClientError>({
    queryKey: ['wellness'],
    queryFn: () => artistFetch<WellnessSummary>('/wellness/summary'),
  })

  const climate = useQuery<ClimateProfile, ApiClientError>({
    queryKey: ['wellness-climate'],
    queryFn: () => artistFetch<ClimateProfile>('/wellness/climate/profile'),
  })

  return (
    <div>
      <PageHeader
        title="Wellness"
        subtitle="Tour density, fatigue scoring, climate guardrails, and schedule protection."
      />

      <div className="space-y-6">
        {/* ── Dashboard cards ── */}
        <AsyncBoundary isLoading={summary.isLoading} error={summary.error}>
          {summary.data && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatCard
                label="Fatigue score"
                value={`${summary.data.fatigueScore}/100`}
                color={
                  summary.data.fatigueScore > 70 ? 'red' :
                  summary.data.fatigueScore > 40 ? 'amber' : 'emerald'
                }
              />
              <StatCard label="Consecutive travel days" value={summary.data.consecutiveTravelDays} />
              <StatCard label="Rest days scheduled" value={summary.data.restDaysScheduled} />
            </div>
          )}
        </AsyncBoundary>

        {/* ── Climate blocks ── */}
        <AsyncBoundary isLoading={summary.isLoading} error={null}>
          {summary.data && summary.data.upcomingClimateBlocks.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-6">
              <h2 className="mb-3 flex items-center gap-2 font-display text-xl text-amber-900">
                <AlertTriangle className="h-5 w-5" />
                Climate blocks (next 90 days)
              </h2>
              <ul className="space-y-2">
                {summary.data.upcomingClimateBlocks.map((b) => (
                  <li key={b.showId} className="flex items-start gap-2 text-sm text-amber-900">
                    <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-amber-500" />
                    <span><strong>{b.city}</strong> — {b.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </AsyncBoundary>

        {/* ── Climate profile configuration ── */}
        <AsyncBoundary isLoading={climate.isLoading} error={climate.error}>
          {climate.data && (
            <ClimateConfigPanel
              profile={climate.data}
              onSaved={() => {
                qc.invalidateQueries({ queryKey: ['wellness-climate'] })
                qc.invalidateQueries({ queryKey: ['wellness'] })
              }}
            />
          )}
        </AsyncBoundary>
      </div>
    </div>
  )
}

/* ── Climate configuration panel ── */

function ClimateConfigPanel({
  profile,
  onSaved,
}: {
  profile: ClimateProfile
  onSaved: () => void
}) {
  const [minTemp, setMinTemp] = useState(profile.minTempCelsius)
  const [noSnow, setNoSnow] = useState(profile.noSnowOrFrost)
  const [outdoorBuffer, setOutdoorBuffer] = useState(profile.outdoorBufferCelsius)
  const [blocked, setBlocked] = useState<number[]>(profile.blockedMonths)
  const [preferred, setPreferred] = useState<number[]>(profile.preferredMonths)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  function toggleMonth(month: number, list: number[], setList: (v: number[]) => void) {
    setList(list.includes(month) ? list.filter((m) => m !== month) : [...list, month])
  }

  async function save() {
    setSaving(true)
    setSaved(false)
    try {
      await artistFetch('/wellness/climate/profile', {
        method: 'PATCH',
        body: {
          minTempCelsius: minTemp,
          noSnowOrFrost: noSnow,
          outdoorBufferCelsius: outdoorBuffer,
          blockedMonths: blocked,
          preferredMonths: preferred,
        },
      })
      setSaved(true)
      onSaved()
    } catch (err) {
      alert(err instanceof ApiClientError ? err.message : 'Error saving')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-6">
      <h2 className="mb-4 font-display text-xl">Climate preferences</h2>
      <p className="mb-6 text-sm text-ovation-muted">
        When a show is proposed in a city that violates these thresholds, Ovation flags it
        as a climate-blocked date. These are soft blocks — the tour manager can override.
      </p>

      <div className="space-y-6">
        {/* Temperature */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ovation-muted">
              <Thermometer className="h-3.5 w-3.5" />
              Minimum temperature
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={minTemp}
                onChange={(e) => setMinTemp(Number(e.target.value))}
                className="w-20 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
                min={-20}
                max={40}
              />
              <span className="text-sm text-ovation-muted">
                {'\u00B0'}C / {Math.round(minTemp * 9 / 5 + 32)}{'\u00B0'}F
              </span>
            </div>
          </div>
          <div>
            <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ovation-muted">
              <Sun className="h-3.5 w-3.5" />
              Outdoor show buffer
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={outdoorBuffer}
                onChange={(e) => setOutdoorBuffer(Number(e.target.value))}
                className="w-20 rounded-md border border-ovation-ink/15 px-3 py-2 text-sm"
                min={0}
                max={20}
              />
              <span className="text-sm text-ovation-muted">
                {'\u00B0'}C above minimum for outdoor shows
              </span>
            </div>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 rounded-md border border-ovation-ink/10 px-3 py-2 cursor-pointer hover:bg-ovation-ink/5">
              <input
                type="checkbox"
                checked={noSnow}
                onChange={(e) => setNoSnow(e.target.checked)}
                className="accent-ovation-ink"
              />
              <div>
                <div className="flex items-center gap-1 text-sm font-medium">
                  <Wind className="h-3.5 w-3.5" />
                  No snow or frost
                </div>
                <div className="text-xs text-ovation-muted">Block dates with freezing conditions</div>
              </div>
            </label>
          </div>
        </div>

        {/* Blocked months */}
        <div>
          <label className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ovation-muted">
            <Calendar className="h-3.5 w-3.5" />
            Blocked months (no touring)
          </label>
          <div className="flex flex-wrap gap-2">
            {MONTH_NAMES.map((name, i) => (
              <button
                key={i}
                type="button"
                onClick={() => toggleMonth(i, blocked, setBlocked)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  blocked.includes(i)
                    ? 'bg-red-500 text-white'
                    : 'border border-ovation-ink/15 text-ovation-muted hover:bg-ovation-ink/5'
                }`}
              >
                {name.slice(0, 3)}
              </button>
            ))}
          </div>
        </div>

        {/* Preferred months */}
        <div>
          <label className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ovation-muted">
            <Sun className="h-3.5 w-3.5" />
            Preferred touring months
          </label>
          <div className="flex flex-wrap gap-2">
            {MONTH_NAMES.map((name, i) => (
              <button
                key={i}
                type="button"
                onClick={() => toggleMonth(i, preferred, setPreferred)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  preferred.includes(i)
                    ? 'bg-emerald-500 text-white'
                    : 'border border-ovation-ink/15 text-ovation-muted hover:bg-ovation-ink/5'
                }`}
              >
                {name.slice(0, 3)}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-ovation-muted">
            Shows outside preferred months get a warning (not a block).
          </p>
        </div>

        {/* Save */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save climate preferences'}
          </button>
          {saved && <span className="text-sm text-emerald-700">Saved.</span>}
        </div>
      </div>
    </div>
  )
}

/* ── Stat card ── */

function StatCard({
  label,
  value,
  color = 'default',
}: {
  label: string
  value: string | number
  color?: 'default' | 'red' | 'amber' | 'emerald'
}) {
  const colorClass =
    color === 'red' ? 'text-red-600' :
    color === 'amber' ? 'text-amber-600' :
    color === 'emerald' ? 'text-emerald-600' : ''

  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-5">
      <div className="text-xs uppercase tracking-wider text-ovation-muted">{label}</div>
      <div className={`mt-2 font-display text-3xl ${colorClass}`}>{value}</div>
    </div>
  )
}
